import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import {
  IFundingChart,
  IFuturesFundingRateData,
  IFuturesOrderBookAllSteps,
  IFuturesOrderBookItem,
} from '@/types/futures';
import { bigNumberObjectCheck, bigNumberObjectCheckByKeys, debounceRequest } from '@/utils';
import { axiosGet } from '@/utils/axios';

import { Amm, Instrument, MinimalPearl, RANGE_SPACING } from '@synfutures/sdks-perp';

import { BigNumber } from 'ethers';

import { SYN_API_URLS } from '@/constants/api';

import { KlineInterval } from '@/types/chart';
import _ from 'lodash';
import moment from 'moment';
import {
  APIKlineDataFromApi,
  FetchAbcFaucetSendTxInput,
  FetchAbcFaucetSendTxResponse,
  FetchFuturesInstrumentInput,
  FetchFuturesInstrumentInquireByNotionalInput,
  FetchFuturesInstrumentInquireByNotionalResponse,
  FetchFuturesInstrumentInquireByTickInput,
  FetchFuturesInstrumentInquireByTickResponse,
  FetchFuturesInstrumentInquireInput,
  FetchFuturesInstrumentInquireResponse,
  FetchFuturesPairDepthChartInput,
  FetchFuturesPairDepthChartResponse,
  FetchFuturesPairFundingChartInput,
  FetchFuturesPairFundingChartResponse,
  FetchFuturesPairKlineChartInput,
  FetchFuturesPairKlineChartResponse,
  FetchFuturesPairOrderBookInput,
  FetchFuturesPairOrderBookResponse,
  FetchInstrumentFromChainInput,
  FetchInstrumentFromChainResponse,
  FetchLiquidityDataInput,
  FetchLiquidityDataResponse,
  InstrumentFromApi,
} from './types';

export async function fetchInstrumentFromChain({
  sdk,
  instrumentAddress,
  expiry,
  block,
}: FetchInstrumentFromChainInput): Promise<FetchInstrumentFromChainResponse> {
  if (sdk) {
    const instrumentList = await sdk.perp.observer.getInstrument(
      [
        {
          instrument: instrumentAddress,
          expiries: expiry ? [expiry] : [],
        },
      ],
      { blockTag: block },
    );
    if (instrumentList.length > 0) {
      const futuresItem = instrumentList[0];

      block = futuresItem.blockInfo?.height || 0;

      return futuresItem;
    }
  }
  return null;
}

export const fetchFuturesInstrument = debounceRequest(
  async ({ chainId, address }: FetchFuturesInstrumentInput): Promise<FetchInstrumentFromChainResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.INSTRUMENT,
      config: { params: { chainId, address } },
    });
    if (res?.data?.data) {
      const instrumentFromApi = res.data.data as InstrumentFromApi;

      return transformFuturesInstrument(instrumentFromApi);
    }
    return null;
  },
  0,
);

export function transformFuturesInstrument(instrumentFromApi: InstrumentFromApi): Instrument {
  const data = bigNumberObjectCheck(instrumentFromApi, [
    // Direct instrument keys
    'spotPrice',
    'minTradeValue',
    'minOrderValue',
    'minRangeValue',
    // Nested AMM keys
    'sqrtPX96',
    'liquidity',
    'totalLiquidity',
    'involvedFund',
    'openInterests',
    'feeIndex',
    'protocolFee',
    'totalLong',
    'totalShort',
    'longSocialLossIndex',
    'shortSocialLossIndex',
    'longFundingIndex',
    'shortFundingIndex',
    'insuranceFund',
    'settlementPrice',
    'markPrice',
    'fairPrice',
    // Nested setting keys
    'minMarginAmount',
    'stabilityFeeRatioParam',
    'tip',
  ]);
  const amms = data.amms;
  return {
    ...data,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    amms: amms.reduce((map: any, amm: any) => {
      map.set(amm.expiry, amm);
      return map;
    }, new Map<number, Amm>()),
  };
}

export const fetchFuturesPairOrderBook = debounceRequest(
  async ({ chainId, address, expiry }: FetchFuturesPairOrderBookInput): Promise<FetchFuturesPairOrderBookResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.ORDER_BOOK,
      config: { params: { chainId, address, expiry } },
    });

    if (res?.data?.data) {
      return transformFuturesPairOrderBook(res?.data?.data);
    }
    return null;
  },
  0,
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function transformFuturesPairOrderBook(data: any): IFuturesOrderBookAllSteps {
  data = bigNumberObjectCheck(data);
  return _.reduce(
    data,
    (acc, depth, key) => {
      // Transform bids and asks to left and right arrays
      const left: IFuturesOrderBookItem[] = _.orderBy(
        (depth.bids || []).map(
          (bid: {
            tick: number;
            price: string;
            baseSize: string;
            quoteSize: string;
            baseSum: string;
            quoteSum: string;
          }) => {
            const price = WrappedBigNumber.from(BigNumber.from(bid.price));
            const base = WrappedBigNumber.from(BigNumber.from(bid.baseSize));
            const quoteSize = WrappedBigNumber.from(BigNumber.from(bid.quoteSize));
            const baseSum = WrappedBigNumber.from(BigNumber.from(bid.baseSum));
            const quoteSum = WrappedBigNumber.from(BigNumber.from(bid.quoteSum));
            return {
              tick: bid.tick,
              price: price,
              base: base,
              quoteSize: quoteSize,
              baseSum: baseSum,
              quoteSum: quoteSum,
            };
          },
        ),
        ['tick'],
        ['desc'],
      );

      const right: IFuturesOrderBookItem[] = _.orderBy(
        (depth.asks || []).map(
          (ask: {
            tick: number;
            price: string;
            baseSize: string;
            quoteSize: string;
            baseSum: string;
            quoteSum: string;
          }) => ({
            tick: ask.tick,
            price: WrappedBigNumber.from(BigNumber.from(ask.price)),
            base: WrappedBigNumber.from(BigNumber.from(ask.baseSize)),
            quoteSize: WrappedBigNumber.from(BigNumber.from(ask.quoteSize)),
            baseSum: WrappedBigNumber.from(BigNumber.from(ask.baseSum)),
            quoteSum: WrappedBigNumber.from(BigNumber.from(ask.quoteSum)),
          }),
        ),
        ['tick'],
        ['desc'],
      );

      acc[key] = { left, right };
      return acc;
    },
    {} as IFuturesOrderBookAllSteps,
  );
}

export const fetchFuturesPairDepthChart = debounceRequest(
  async ({
    chainId,
    address,
    expiry,
    sdk,
  }: // sdk,
  FetchFuturesPairDepthChartInput): Promise<FetchFuturesPairDepthChartResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.DEPTH_CHARTS,
      config: { params: { chainId, address, expiry } },
    });
    if (res?.data?.data) {
      const data = bigNumberObjectCheck(res.data.data, ['sqrtPX96', 'liquidity', 'liquidityNet', 'left']);
      data.tick2Pearl = _.reduce(
        data.tick2Pearl,
        (acc, value, key) => {
          acc.set(Number(key), value);
          return acc;
        },
        new Map() as Map<number, MinimalPearl>,
      );

      const newData = sdk.perpDataSource.depth.getDepthRangeDataByLiquidityDetails(data, data.size, data.stepRatio);

      return newData;
    }
    return null;
  },
  0,
);

export const fetchFuturesPairFundingChart = debounceRequest(
  async ({
    chainId,
    instrumentAddr: instrumentAddr,
    expiry,
    chartDuration,
  }: FetchFuturesPairFundingChartInput): Promise<FetchFuturesPairFundingChartResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.FUNDING_CHARTS,
      config: { params: { chainId, instrument: instrumentAddr, expiry, type: chartDuration } },
    });

    if (res?.data?.data) {
      const dataSource: IFundingChart[] = res?.data?.data.map((d: IFuturesFundingRateData) => {
        return {
          ...d,
          long: Number(d.long),
          short: Number(d.short),
        };
      });
      // if (pair.isInverse) {
      //   dataSource = dataSource.map((d) => {
      //     return { ...d, longFundingRate: d.shortFundingRate, shortFundingRate: d.longFundingRate };
      //   });
      // }
      return dataSource
        .map((d) => {
          const isLongPay = d.long < 0;
          return {
            ...d,
            payRate: WrappedBigNumber.from(isLongPay ? d.long : d.short),
            receiveRate: WrappedBigNumber.from(isLongPay ? d.short : d.long),
            isLongPay: isLongPay,
          };
        })
        .sort((a, b) => a.timestamp - b.timestamp);
    }
    return null;
  },
  0,
);

export const fetchFuturesPairKlineChart = async ({
  chainId,
  instrumentAddress,
  expiry,
  klineInterval: klineInterval,
  timeEnd,
  limit = 1000,
}: FetchFuturesPairKlineChartInput): Promise<FetchFuturesPairKlineChartResponse> => {
  timeEnd = timeEnd || Math.floor(Date.now() / 1000);
  // fetch 1 more minute data to avoid the last candle tick error

  let utcEnd = moment(timeEnd * 1000).second(0);
  if (klineInterval === KlineInterval.DAY) {
    utcEnd = utcEnd.add(1, 'day');
  } else if (klineInterval === KlineInterval.WEEK) {
    utcEnd = utcEnd.add(7, 'day');
  } else if (klineInterval === KlineInterval.MINUTE) {
    utcEnd = utcEnd.add(1, 'minutes');
  } else if (klineInterval === KlineInterval.FIVE_MINUTE) {
    utcEnd = utcEnd.add(5, 'minutes');
  } else if (klineInterval === KlineInterval.FIFTEEN_MINUTE) {
    utcEnd = utcEnd.add(15, 'minutes');
  } else if (klineInterval === KlineInterval.THIRTY_MINUTE) {
    utcEnd = utcEnd.add(30, 'minutes');
  } else if (klineInterval === KlineInterval.HOUR) {
    utcEnd = utcEnd.add(1, 'hours');
  } else if (klineInterval === KlineInterval.FOUR_HOUR) {
    utcEnd = utcEnd.add(4, 'hours');
  }

  const endTs = utcEnd.unix();

  const res = await axiosGet({
    url: SYN_API_URLS.FUTURES.KLINE_CHARTS,
    config: {
      params: {
        chainId,
        instrument: instrumentAddress,
        expiry,
        interval: klineInterval,

        endTime: endTs,
        limit: limit,
      },
    },
  });
  const data = res?.data?.data as APIKlineDataFromApi[];

  return _.uniqBy(
    data.map((d) => ({
      ...d,
      timestamp: d.openTimestamp,
    })) || [],
    'timestamp',
  );
};

export const fetchFuturesInstrumentInquire = debounceRequest(
  async ({
    chainId,
    instrument,
    expiry,
    size,
  }: FetchFuturesInstrumentInquireInput): Promise<FetchFuturesInstrumentInquireResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.INSTRUMENT_INQUIRE,
      config: { params: { chainId, instrument, expiry, size } },
    });
    if (res?.data?.data) {
      const data = bigNumberObjectCheckByKeys(res.data.data, [
        'benchmark',
        'sqrtFairPX96',
        'mark',
        'entryNotional',
        'fee',
        'minAmount',
        'sqrtPostFairPX96',
      ]);

      return {
        ...data,
        tick: Number(data.tick),
        postTick: Number(data.postTick),
      };
    }
    return null;
  },
  0,
);

export const fetchFuturesInstrumentInquireByTick = debounceRequest(
  async ({
    chainId,
    instrument,
    expiry,
    tick,
  }: FetchFuturesInstrumentInquireByTickInput): Promise<FetchFuturesInstrumentInquireByTickResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.INSTRUMENT_INQUIRE_BY_TICK,
      config: { params: { chainId, instrument, expiry, tick } },
    });
    if (res?.data?.data) {
      const data = bigNumberObjectCheckByKeys(res.data.data, [
        'size',
        // under quotation
        'benchmark',
        'sqrtFairPX96',
        'mark',
        'entryNotional',
        'fee',
        'minAmount',
        'sqrtPostFairPX96',
      ]);

      // Return the data in the expected structure
      return {
        size: data.size,
        quotation: {
          benchmark: data.benchmark,
          sqrtFairPX96: data.sqrtFairPX96,
          tick: Number(data.tick),
          mark: data.mark,
          entryNotional: data.entryNotional,
          fee: data.fee,
          minAmount: data.minAmount,
          sqrtPostFairPX96: data.sqrtPostFairPX96,
          postTick: Number(data.postTick),
        },
      };
    }
    return null;
  },
  0,
);

export const fetchFuturesInstrumentInquireByNotional = debounceRequest(
  async ({
    chainId,
    instrument,
    expiry,
    notional,
    long,
  }: FetchFuturesInstrumentInquireByNotionalInput): Promise<FetchFuturesInstrumentInquireByNotionalResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.FUTURES.INSTRUMENT_INQUIRE_BY_NOTIONAL,
      config: { params: { chainId, instrument, expiry, notional, long } },
    });
    if (res?.data?.data) {
      const data = bigNumberObjectCheckByKeys(res.data.data, [
        'size',
        // under quotation
        'benchmark',
        'sqrtFairPX96',
        'mark',
        'entryNotional',
        'fee',
        'minAmount',
        'sqrtPostFairPX96',
      ]);

      // Return the data in the expected structure
      return {
        size: data.size,
        quotation: {
          benchmark: data.benchmark,
          sqrtFairPX96: data.sqrtFairPX96,
          tick: Number(data.tick),
          mark: data.mark,
          entryNotional: data.entryNotional,
          fee: data.fee,
          minAmount: data.minAmount,
          sqrtPostFairPX96: data.sqrtPostFairPX96,
          postTick: Number(data.postTick),
        },
      };
    }
    return null;
  },
  0,
);

/**
 * Fetch ABC faucet send transaction
 * @param to - The recipient address
 * @returns Promise with transaction hash, recipient address and amount
 */
export const fetchAbcFaucetSendTx = debounceRequest(
  async ({ to, chainId }: FetchAbcFaucetSendTxInput): Promise<FetchAbcFaucetSendTxResponse> => {
    const res = await axiosGet({
      url: SYN_API_URLS.OTHER.ABC_FAUCET_SEND_TX,
      config: { params: { to, chainId } },
    });
    if (res?.data?.data) {
      return res.data.data;
    }
    return null;
  },
  0,
);

export async function fetchLiquidityData(params: FetchLiquidityDataInput): Promise<FetchLiquidityDataResponse> {
  const { chainId, instrument, expiry, rangeSpacing = RANGE_SPACING } = params;
  const res = await axiosGet({
    url: SYN_API_URLS.FUTURES.LIQUIDITY_CHARTS,
    config: { params: { chainId, instrument, expiry, rangeSpacing } },
  });
  if (res?.data?.data) {
    return res.data.data;
  }
  return null;
}
